import { onUnmounted, watch, type Ref } from 'vue'
import useResearchCustomerStore from '../stores/useResearchCustomerStore'

/** Espelha o widget Pulse: complete logo após reveal (< 1s) = rehydratação, não conclusão do usuário. */
const PULSE_COMPLETE_MIN_VISIBLE_MS = 1000

type PulseMessagePayload = {
  source?: string
  tipo?: string
  type?: string
}

type DismissReason = 'x' | 'esc' | 'backdrop' | 'mute'

function parsePulseMessagePayload (data: unknown): PulseMessagePayload | null {
  if (data == null) {
    return null
  }

  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data)
      if (parsed != null && typeof parsed === 'object') {
        return parsed as PulseMessagePayload
      }
      return null
    } catch {
      return null
    }
  }

  if (typeof data === 'object') {
    return data as PulseMessagePayload
  }

  return null
}

function getPulseEventType (payload: PulseMessagePayload | null): string | undefined {
  return payload?.tipo ?? payload?.type
}

function isPulseCompleteMessage (payload: PulseMessagePayload | null): boolean {
  return payload?.source === 'pulse' && getPulseEventType(payload) === 'complete'
}

function getPulseBackdrop (): HTMLElement | null {
  const backdrop = document.querySelector('.__pulse-backdrop')
  return backdrop instanceof HTMLElement ? backdrop : null
}

function isPulseModalRevealed (backdrop: HTMLElement | null = getPulseBackdrop()): boolean {
  if (backdrop == null) {
    return false
  }

  return backdrop.classList.contains('__pulse-aberto') && backdrop.style.opacity === '1'
}

export function usePulseResearchBridge (options: {
  armed: Ref<boolean>
  onSubmitted: () => void
}): void {
  const store = useResearchCustomerStore()
  let submitting = false
  let submitted = false
  let listenersAttached = false
  let pulseModalWasOpen = false
  let pulseModalRevealed = false
  let pulseModalRevealedAt: number | null = null
  let _dismissReason: DismissReason | null = null
  let modalObserver: MutationObserver | undefined

  function markModalRevealed (): void {
    pulseModalRevealed = true
    pulseModalWasOpen = true
    if (pulseModalRevealedAt == null) {
      pulseModalRevealedAt = Date.now()
    }
  }

  function canSubmitForComplete (): boolean {
    if (!pulseModalRevealed && !isPulseModalRevealed()) {
      return false
    }

    if (pulseModalRevealedAt == null) {
      return false
    }

    return Date.now() - pulseModalRevealedAt >= PULSE_COMPLETE_MIN_VISIBLE_MS
  }

  async function submitDismiss (): Promise<void> {
    if (!options.armed.value || submitting || submitted) {
      return
    }

    submitting = true
    submitted = true

    try {
      const ok = await store.submitActive()
      if (!ok) {
        submitted = false
        return
      }
      options.onSubmitted()
    } finally {
      submitting = false
    }
  }

  async function onMessage (event: MessageEvent): Promise<void> {
    if (!options.armed.value || submitted) {
      return
    }

    const payload = parsePulseMessagePayload(event.data)
    if (payload?.source !== 'pulse') {
      return
    }

    const eventType = getPulseEventType(payload)

    if (eventType === 'ready') {
      return
    }

    if (!isPulseCompleteMessage(payload)) {
      return
    }

    if (!canSubmitForComplete()) {
      event.stopImmediatePropagation()
      return
    }

    await submitDismiss()
  }

  function onKeydown (event: KeyboardEvent): void {
    if (!options.armed.value || submitted) {
      return
    }

    if (event.key === 'Escape') {
      _dismissReason = 'esc'
    }
  }

  async function onDocumentClick (event: MouseEvent): Promise<void> {
    if (!options.armed.value || submitted) {
      return
    }

    const target = event.target
    if (!(target instanceof Element)) {
      return
    }

    if (target.closest('.__pulse-mute-modal') != null || target.closest('.__pulse-sheet-mute') != null) {
      _dismissReason = 'mute'
      return
    }

    const closeBtn = target.closest('.__pulse-fechar')
    if (closeBtn != null) {
      if (closeBtn.closest('.__pulse-backdrop') == null) {
        return
      }
      _dismissReason = 'x'
      await submitDismiss()
      return
    }

    if (target.classList.contains('.__pulse-backdrop')) {
      _dismissReason = 'backdrop'
    }
  }

  function syncPulseModalState (): void {
    if (!options.armed.value || submitted) {
      return
    }

    const backdrop = getPulseBackdrop()
    const isOpen = backdrop?.classList.contains('__pulse-aberto') ?? false

    if (isOpen && isPulseModalRevealed(backdrop)) {
      markModalRevealed()
      return
    }

    if (isOpen) {
      pulseModalWasOpen = true
      return
    }

    if (!pulseModalWasOpen) {
      return
    }

    pulseModalWasOpen = false
    pulseModalRevealed = false
    pulseModalRevealedAt = null
    _dismissReason = null
  }

  function attachListeners (): void {
    if (listenersAttached) {
      return
    }

    window.addEventListener('message', onMessage, { capture: true })
    window.addEventListener('keydown', onKeydown, { capture: true })
    document.addEventListener('click', onDocumentClick, { capture: true })

    modalObserver = new MutationObserver(() => {
      syncPulseModalState()
    })
    modalObserver.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    })

    syncPulseModalState()
    listenersAttached = true
  }

  function detachListeners (): void {
    if (!listenersAttached) {
      return
    }

    window.removeEventListener('message', onMessage, { capture: true })
    window.removeEventListener('keydown', onKeydown, { capture: true })
    document.removeEventListener('click', onDocumentClick, { capture: true })
    modalObserver?.disconnect()
    modalObserver = undefined

    pulseModalWasOpen = false
    pulseModalRevealed = false
    pulseModalRevealedAt = null
    _dismissReason = null
    submitting = false
    submitted = false
    listenersAttached = false
  }

  watch(options.armed, (isArmed) => {
    if (isArmed) {
      attachListeners()
    } else {
      detachListeners()
    }
  }, { immediate: true })

  onUnmounted(() => {
    detachListeners()
  })
}
